import React, { FC, useEffect, useState } from 'react';
import { Addon, AddonCategory, ShopContent } from '../../../types/cms_shared_types';
import { Step } from '../Step';
import { Formik } from 'formik';
import { DefaultBottomBar } from '../BottomBar/DefaultBottomBar';
import { AddonListActions } from '../../../types/webshop';
import { AddonCategoryPicker } from './AddonCategoryPicker';
import { AddonList } from './AddonList';
import { findSelectedAddonCategoryByKey } from '../../../helper/cms';
import { SidebarTip } from '../SidebarTip';
import { Conditional } from '../../Common/Conditional';

export interface AddonsFormInput {
  selectedAddonCategoryKey?: string;
}

interface AddonsProps {
  selectedAddonCategoryKey?: string;
  setSelectedAddonCategoryKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
  nextButtonClicked: () => void;
  cmsShopContent: ShopContent;
  getPriceSummary: () => React.ReactElement;
  summaryFormComment: string;
  setSummaryFormComment: React.Dispatch<React.SetStateAction<string>>;
  displayUpdatedPricing: boolean;
  setDisplayUpdatedPricing: (value: boolean) => void;
}

export const Addons: FC<AddonsProps> = ({
  selectedAddonCategoryKey,
  setSelectedAddonCategoryKey,
  selectedAddons,
  selectedAddonsActions,
  nextButtonClicked,
  cmsShopContent,
  getPriceSummary,
  summaryFormComment,
  setSummaryFormComment,
  displayUpdatedPricing,
  setDisplayUpdatedPricing,
}) => {
  const [selectedAddonCategory, setSelectedAddonCategory] = useState<AddonCategory>();

  useEffect(() => {
    if (cmsShopContent.addonCategories.length > 0 && selectedAddonCategoryKey === undefined) {
      setSelectedAddonCategoryKey(cmsShopContent.addonCategories[0].key);
    }
  }, [cmsShopContent.addonCategories, selectedAddonCategoryKey, setSelectedAddonCategoryKey]);

  useEffect(() => {
    if (selectedAddonCategoryKey) {
      setSelectedAddonCategory(
        findSelectedAddonCategoryByKey(selectedAddonCategoryKey, cmsShopContent.addonCategories),
      );
    }
  }, [selectedAddonCategoryKey, cmsShopContent.addonCategories]);

  return (
    <Formik
      onSubmit={async (values) => {
        if (values.summaryFormInputs) {
          await setSummaryFormComment(values.summaryFormInputs);
        }
        setDisplayUpdatedPricing(false);
        nextButtonClicked();
      }}
      initialValues={{
        selectedAddonCategoryKey,
        summaryFormInputs: summaryFormComment,
      }}
      validateOnMount
      validateOnChange
    >
      {({ submitForm, isValid }) => (
        <Step
          fullWidth={!displayUpdatedPricing}
          title={displayUpdatedPricing ? cmsShopContent.addonsStep.updatedPricing : cmsShopContent.addonsStep.title}
          subtitle={displayUpdatedPricing ? cmsShopContent.addonsStep.includedProducts : ''}
          content={
            <Conditional
              condition={!displayUpdatedPricing}
              trueRender={
                <div className="flex flex-col sm:flex-row sm:justify-center">
                  <div className="sm:flex sm:flex-col sm:w-1/4 max-w-[600px]">
                    <div className="sm:w-fit sm:flex sm:flex-col sm:self-end">
                      <AddonCategoryPicker
                        addonCategories={cmsShopContent.addonCategories}
                        cmsShopContent={cmsShopContent}
                        selectedAddonCategoryKey={selectedAddonCategoryKey}
                        setSelectedAddonCategoryKey={setSelectedAddonCategoryKey}
                      />
                    </div>
                  </div>
                  {selectedAddonCategory ? (
                    <div className="flex flex-col items-center sm:max-w-1/2 sm:mx-5 lg:mx-14">
                      <div className="block ml-auto ">
                        <AddonList
                          addonCategory={selectedAddonCategory}
                          selectedAddons={selectedAddons}
                          selectedAddonsActions={selectedAddonsActions}
                          cmsShopContent={cmsShopContent}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="w-1/4 max-w-[600px] hidden sm:block">
                    <SidebarTip
                      tipTitleBefore={cmsShopContent.addonsStep.tipTitle}
                      tipContentBefore={cmsShopContent.addonsStep.tipContent}
                    />
                  </div>
                </div>
              }
              falseRender={getPriceSummary()}
            />
          }
          bottomBar={
            <DefaultBottomBar
              continueButtonDisabled={!isValid}
              onClick={() => {
                if (!displayUpdatedPricing && selectedAddons.length > 0) {
                  setDisplayUpdatedPricing(true);
                  return;
                }

                submitForm();
              }}
              cmsShopContent={cmsShopContent}
              footerContent={cmsShopContent.addonsStep.footer}
            />
          }
        />
      )}
    </Formik>
  );
};
